import { Injectable } from '@angular/core';
import io from 'socket.io-client';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BrowserStorageService } from 'src/app/services/browser-storage.service';

@Injectable({
    providedIn: 'root'
})
export class SocketService {
    /** localStorage key holding the current workspace (merchant) id. */
    private static readonly WORKSPACE_KEY = 'merchant_id';

    socket: any;
    quoteSocket: any;

    constructor(private storage: BrowserStorageService) {
        this.socket = io(environment.API_URL, {
            transports: ['websocket', 'polling'],
            forceNew: false,
            ...this.workspaceSocketOptions()
        });
        // Re-announce the workspace on every (re)connect: a safety net next to the handshake auth.
        this.socket.on('connect', () => this.announceWorkspace(this.socket));

        // Connect to the quotes service socket server.
        // QUOTE_SOCKET_URL is the host origin. We supply path: '/quotes_management/socket.io'
        // to match the reverse proxy path routing on dev/production.
        if ((environment as any).QUOTE_SOCKET_URL) {
            // The quotes service delivers per workspace too (card #272): name it the same way.
            this.quoteSocket = io((environment as any).QUOTE_SOCKET_URL, {
                path: '/quotes_management/socket.io',
                transports: ['websocket', 'polling'],
                forceNew: true,
                ...this.workspaceSocketOptions()
            });
            this.quoteSocket.on('connect', () => this.announceWorkspace(this.quoteSocket));
        }
    }

    /**
     * merch-backend delivers live updates per workspace (socket.io rooms): every connection
     * names the current workspace. `auth` is a function so it is re-read on every reconnect.
     */
    workspaceSocketOptions() {
        return {
            auth: (cb: (data: { merchantId: string | null }) => void) =>
                cb({ merchantId: this.storage.getString(SocketService.WORKSPACE_KEY) })
        };
    }

    /**
     * Re-announce the current workspace (e.g. after the user switched workspace). The server
     * moves the socket out of any previous workspace room, so only one workspace is active.
     * Only on a live connection: an emit made while disconnected would be buffered and replayed
     * AFTER the fresh join on reconnect, putting the socket back in a stale workspace. The
     * 'connect' handler announces the current workspace on every (re)connect instead.
     */
    joinCurrentWorkspace(): void {
        this.announceWorkspace(this.socket);
        this.announceWorkspace(this.quoteSocket);
    }

    private announceWorkspace(socket: any): void {
        const merchantId = this.storage.getString(SocketService.WORKSPACE_KEY);
        if (merchantId && socket?.connected) {
            socket.emit('join-workspace', merchantId);
        }
    }

    /**
     * Drop the workspace (logout): reconnect so the handshake carries no workspace and the
     * server puts the socket in no room. Call after `merchant_id` has been removed.
     */
    resetWorkspace(): void {
        this.socket?.disconnect();
        this.socket?.connect();
        this.quoteSocket?.disconnect();
        this.quoteSocket?.connect();
    }

    listen(Eventname: string) {
        return new Observable((subscriber) => {
            // Announce the workspace when a page actually subscribes (covers a workspace switch
            // without a reload, since pages subscribe again on load).
            this.joinCurrentWorkspace();
            const handler = (data) => {
                subscriber.next(data);
            };
            
            this.socket.on(Eventname, handler);
            
            if (this.quoteSocket) {
                this.quoteSocket.on(Eventname, handler);
            }
            
            // Cleanup: remove the specific handler when the observable is unsubscribed
            return () => {
                this.socket.off(Eventname, handler);
                if (this.quoteSocket) {
                    this.quoteSocket.off(Eventname, handler);
                }
            };
        });
    }
}